/**
 * The PiP session widget (#1307 — grammar v4 §15.6, CP-11/34, C-6).
 *
 * The §15 voice stage at reduced scale, floating over the conversation: the
 * Owner watches their own words arrive in the thread while they speak. The
 * widget shows ONLY state — the transcript is the record and lives in the
 * thread (§15.6 rule 4).
 *
 * Geometry (expanded): raised surface, radius 16, max-w 240px; disc lg 40
 * with a 2px state ring at Ø52 and a scaled glow; state label + `.tnum`
 * timer; three 44px controls (mute / end / minimize). Minimized: a 44px
 * pill — disc sm 24 + state ring + timer; the whole pill is the expand
 * target. Both brands wear it through their preset tokens (halo-*, chat-*,
 * agent-*) — this file paints nothing.
 *
 * A11y (§15.6 rule 6): one `role="region"` labeled by session state; state
 * announces politely via the label’s aria-live (never a second live region);
 * controls keyboard-reachable in natural tab order; the minimized pill keeps
 * an accessible name carrying state + elapsed time. Reduced motion: rings
 * freeze, the glow is static (§15.3.5).
 */
import React, { useEffect, useState } from 'react'
import { chatConfig, chatCopy } from '../../config'
import { isLive, type VoiceSnapshot } from './types'

export interface PipWidgetProps {
  snapshot: VoiceSnapshot
  onMuteToggle: () => void
  onEnd: () => void
  /** Clear an ended/failed widget (parent decides when it leaves the tree). */
  onDismiss?: () => void
  /**
   * #1318: start again after a failure. Optional — a brand that has no
   * retry affordance simply omits it and the failed widget offers dismiss
   * alone, as before.
   */
  onRetry?: () => void
  /** Extra positioning classes; default pins bottom-right above the composer. */
  className?: string
  /**
   * The host does not yet know who the agent is — the identity source (on TRF,
   * the family's persona preference) is still resolving. The disc paints NO
   * letter until it does, rather than the initial of a default name that is
   * about to be replaced (#1806).
   */
  identityPending?: boolean
}

function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000))
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false)
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    let query: MediaQueryList
    try {
      query = window.matchMedia('(prefers-reduced-motion: reduce)')
    } catch {
      return
    }
    setReduced(query.matches)
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches)
    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', onChange)
      return () => query.removeEventListener('change', onChange)
    }
    return
  }, [])
  return reduced
}

/** Ticks once a second while the session is live so the timer moves. */
function useElapsedMs(connectedAt: number | null, running: boolean): number {
  const [elapsed, setElapsed] = useState(0)
  useEffect(() => {
    if (!running || connectedAt === null) return
    setElapsed(Date.now() - connectedAt)
    const t = setInterval(() => setElapsed(Date.now() - connectedAt), 1000)
    return () => clearInterval(t)
  }, [connectedAt, running])
  return connectedAt === null ? 0 : elapsed
}

function ringClass(snapshot: VoiceSnapshot): string {
  if (snapshot.state === 'failed') return 'border-halo-error'
  if (snapshot.state === 'speaking') return 'border-halo-speaking'
  if (snapshot.state === 'listening') {
    return snapshot.muted ? 'border-chat-rule-strong' : 'border-halo-listening'
  }
  if (snapshot.state === 'ended') return 'border-chat-rule-strong'
  return 'border-halo-connect'
}

function glowClass(snapshot: VoiceSnapshot): string {
  if (snapshot.state === 'speaking') return 'bg-halo-speaking'
  if (snapshot.state === 'listening' && !snapshot.muted) return 'bg-halo-listening'
  return 'bg-halo-connect'
}

function stateLabel(snapshot: VoiceSnapshot): string {
  const copy = chatCopy().voice
  switch (snapshot.state) {
    case 'requesting':
      return copy.states.requesting
    case 'connecting':
      return snapshot.slow ? copy.states.connectingSlow : copy.states.connecting
    case 'listening':
      return snapshot.muted ? copy.states.muted : copy.states.listening
    case 'speaking':
      return copy.states.speaking
    case 'ended':
      // #1318: hand the brand the raw duration — the wording (and whether a
      // sub-minute session says "sec") belongs to the brand’s copy pack.
      return copy.states.ended(snapshot.durationMs ?? 0)
    case 'failed':
      switch (snapshot.failure) {
        case 'mic-denied':
          return copy.failures.micDenied
        case 'mic-missing':
          return copy.failures.micMissing
        case 'agent-absent':
          return copy.failures.agentAbsent
        case 'network':
          return copy.failures.network
        default:
          return copy.failures.unavailable
      }
    default:
      return ''
  }
}

export function PipWidget({
  snapshot,
  onMuteToggle,
  onEnd,
  onDismiss,
  onRetry,
  className,
  identityPending = false,
}: PipWidgetProps): React.ReactElement | null {
  const copy = chatCopy().voice
  const reducedMotion = usePrefersReducedMotion()
  const [minimized, setMinimized] = useState(false)
  const live = isLive(snapshot.state)
  const talking = snapshot.state === 'listening' || snapshot.state === 'speaking'
  const elapsedMs = useElapsedMs(snapshot.connectedAt, talking)
  const label = stateLabel(snapshot)
  const timer = talking
    ? formatElapsed(elapsedMs)
    : snapshot.state === 'ended'
      ? formatElapsed(snapshot.durationMs ?? 0)
      : null
  // #1806: an unknown identity paints an empty disc, not a placeholder — the
  // disc is a persona mark, and a wrong letter is worse than no letter.
  const agentInitial = identityPending
    ? null
    : (chatConfig().agentDisplayName() || '?').charAt(0).toUpperCase()

  // §15.6 rule 5: the ended widget shows "Ended · N min" then dissolves on
  // its own — no celebration, no rating prompt, no lingering chrome. Failed
  // states stay until dismissed (the Owner should read the reason).
  const onDismissRef = React.useRef(onDismiss)
  onDismissRef.current = onDismiss
  useEffect(() => {
    if (snapshot.state !== 'ended') return
    const t = setTimeout(() => onDismissRef.current?.(), 4000)
    return () => clearTimeout(t)
  }, [snapshot.state])

  if (snapshot.state === 'idle') return null

  const connectingSpin =
    !reducedMotion && (snapshot.state === 'requesting' || snapshot.state === 'connecting')
  const breathing = !reducedMotion && snapshot.state === 'speaking'
  const position = className ?? 'fixed bottom-20 right-4 z-20'

  if (minimized) {
    // 44px pill: disc sm 24 + state ring + timer; the whole pill expands.
    return (
      <div role="region" aria-label={copy.regionLabel} className={position}>
        <button
          type="button"
          onClick={() => setMinimized(false)}
          aria-label={`${copy.controls.expand}: ${label}${timer ? ` · ${timer}` : ''}`}
          className="flex h-11 items-center gap-2 rounded-full bg-chat-raised px-3 shadow-lg border border-chat-rule"
        >
          <span className="relative inline-flex h-7 w-7 items-center justify-center">
            <span
              aria-hidden="true"
              className={`absolute inset-0 rounded-full border-2 ${ringClass(snapshot)} ${connectingSpin ? 'animate-pip-orbit border-t-transparent' : ''}`}
            />
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-agent-disc text-agent-disc-ink text-caption font-medium">
              {agentInitial}
            </span>
          </span>
          {timer ? <span className="text-caption tabular-nums text-chat-ink-2">{timer}</span> : null}
          <span aria-live="polite" className="sr-only">
            {label}
          </span>
        </button>
      </div>
    )
  }

  return (
    <div
      role="region"
      aria-label={copy.regionLabel}
      className={`${position} w-60 max-w-[240px] rounded-editorial bg-chat-raised p-3 shadow-xl border border-chat-rule`}
    >
      <div className="flex items-center gap-3">
        <span className="relative inline-flex h-[52px] w-[52px] shrink-0 items-center justify-center">
          {/* the §15.1 halo, scaled: soft glow behind the disc */}
          <span
            aria-hidden="true"
            className={`absolute -inset-2 rounded-full opacity-20 blur-md ${glowClass(snapshot)} ${breathing ? 'animate-pip-breath' : ''}`}
          />
          {/* 2px state ring at Ø52 */}
          <span
            aria-hidden="true"
            className={`absolute inset-0 rounded-full border-2 ${ringClass(snapshot)} ${connectingSpin ? 'animate-pip-orbit border-t-transparent' : ''}`}
          />
          {/* disc lg 40: agent persona initial */}
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-agent-disc text-agent-disc-ink text-body font-medium">
            {agentInitial}
          </span>
        </span>
        <div className="min-w-0">
          {/* #1318: state WORDS truncate (they never overflow anyway); a
              failure LINE is prose the Owner has to act on — "Allow it there,
              then try again" is worthless as an ellipsis, so it wraps. */}
          <p
            aria-live="polite"
            className={
              snapshot.state === 'failed'
                ? 'text-body-sm text-chat-ink'
                : 'truncate text-body-sm text-chat-ink'
            }
          >
            {label}
          </p>
          {timer ? (
            <p className="text-caption tabular-nums text-chat-ink-3">{timer}</p>
          ) : null}
        </div>
      </div>

      <div className="mt-2 flex items-center justify-end gap-1">
        {live ? (
          <>
            <button
              type="button"
              onClick={onMuteToggle}
              aria-label={snapshot.muted ? copy.controls.unmute : copy.controls.mute}
              aria-pressed={snapshot.muted}
              className="flex h-11 w-11 items-center justify-center rounded-full text-chat-ink-2 hover:bg-chat-subtle focus-dual"
            >
              {snapshot.muted ? <MicOffIcon /> : <MicIcon />}
            </button>
            <button
              type="button"
              onClick={onEnd}
              aria-label={copy.controls.end}
              className="flex h-11 w-11 items-center justify-center rounded-full text-danger hover:bg-danger-wash focus-dual"
            >
              <PhoneOffIcon />
            </button>
            <button
              type="button"
              onClick={() => setMinimized(true)}
              aria-label={copy.controls.minimize}
              className="flex h-11 w-11 items-center justify-center rounded-full text-chat-ink-2 hover:bg-chat-subtle focus-dual"
            >
              <ChevronDownIcon />
            </button>
          </>
        ) : (
          <>
            {snapshot.state === 'failed' && onRetry ? (
              <button
                type="button"
                onClick={onRetry}
                className="flex h-11 items-center rounded-full px-3 text-body-sm text-chat-accent hover:bg-chat-subtle focus-dual"
              >
                {copy.controls.retry}
              </button>
            ) : null}
            {onDismiss ? (
              <button
                type="button"
                onClick={onDismiss}
                aria-label={copy.controls.minimize}
                className="flex h-11 w-11 items-center justify-center rounded-full text-chat-ink-2 hover:bg-chat-subtle focus-dual"
              >
                <XIcon />
              </button>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}

/* Inline 20px stroke icons — geometry only, color inherits from the button. */
function MicIcon() {
  return (
    <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" x2="12" y1="19" y2="22" />
    </svg>
  )
}
function MicOffIcon() {
  return (
    <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="2" x2="22" y1="2" y2="22" />
      <path d="M18.89 13.23A7.12 7.12 0 0 0 19 12v-2" />
      <path d="M5 10v2a7 7 0 0 0 12 5" />
      <path d="M15 9.34V5a3 3 0 0 0-5.68-1.33" />
      <path d="M9 9v3a3 3 0 0 0 5.12 2.12" />
      <line x1="12" x2="12" y1="19" y2="22" />
    </svg>
  )
}
function PhoneOffIcon() {
  return (
    <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-3.33-2.67m-2.67-3.34a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91" />
      <line x1="22" x2="2" y1="2" y2="22" />
    </svg>
  )
}
function ChevronDownIcon() {
  return (
    <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m6 9 6 6 6-6" />
    </svg>
  )
}
function XIcon() {
  return (
    <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  )
}
